/* ======================================================================== */
/* 35. EffectDistortion */
/* ======================================================================== */
class EffectDistortion extends BaseGLAnimation {

	constructor({
		slider,
		canvas,
		aspect = 1.5,
		displacementImage,
		items,
		retinaEnabled = false
	}) {
		super({
			canvas,
			aspect,
			retinaEnabled
		});

		this.aspect = aspect;
		this.canvas = canvas;
		this.dispImage = displacementImage;
		this.items = items;
		this.slider = slider;
		this.textures = [];
		this.timeline = new gsap.timeline();

		this.disp = this.loader.load(this.dispImage);
		this.disp.wrapS = this.disp.wrapT = THREE.RepeatWrapping;

		this.scene = this._getScene();
		this.viewport = this._getViewport();
		this.camera = this._getCamera();

		this.uniforms = {
			effectFactor: {
				type: "f",
			},
			dispFactor: {
				type: "f",
				value: 0.0
			},
			texture: {
				type: "t",
				value: this.items[0].texture
			},
			texture2: {
				type: "t",
				value: this.items[1] ? this.items[1] : this.items[0].texture // only one slide
			},
			disp: {
				type: "t",
				value: this.disp
			}
		};
		this.geometry = this._getPlaneBufferGeometry();
		this.material = this._getShaderMaterial();
		this.plane = this._getPlane({
			geometry: this.geometry,
			material: this.material
		});
		this.scene.add(this.plane);
		this.initialProgress = 0;
		this.progress = 0;

		this.camera.position.z = 1;
		this.camera.updateProjectionMatrix();
		this._updateScene();

		this._loadTextures().then(() => {
			this.isLoaded = true;

			if (window.$pagePreloader && window.$pagePreloader.length && window.$pagePreloader.is(':visible')) {
				window.$window.on('arts/preloader/end', () => {
					this._animateInitial();
				});
			} else {
				this._animateInitial();
			}
		});
	}

	_animateInitial(delay = 0) {
		this.change({
			from: this.slider.realIndex,
			to: this.slider.realIndex,
			delay,
			speed: parseFloat(this.slider.params.speed / 1000),
			onComplete: () => {

				this.material.uniforms.texture.value = this.items[this.slider.realIndex + 1] ? this.items[this.slider.realIndex + 1].texture : this.items[0].texture; // only one slide
				if (this.slider.params.mousewheel.enabled) {
					this.slider.mousewheel.enable();
				}
				if (this.slider.params.keyboard.enabled) {
					this.slider.keyboard.enable();
				}
				if (this.slider.params.autoplay.enabled) {
					this.slider.autoplay.start();
				}
			}
		});
	}

	_getPlaneBufferGeometry() {
		const {
			width,
			height
		} = this._calculatePosition();

		return new THREE.PlaneBufferGeometry(
			width,
			height,
			this.aspect
		);
	}

	_getCamera() {
		const {
			width,
			height
		} = this._calculatePosition();

		return new THREE.OrthographicCamera(
			width / -2,
			width / 2,
			height / 2,
			height / -2,
		);
	}

	_getVertexShader() {
		return `
			varying vec2 vUv;
			void main() {
				vUv = uv;
				gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			}
		`;
	}

	_getFragmentShader(id) {
		switch (id) {
			case 'slider-textures-horizontal-fs':
				return `
					varying vec2 vUv;

					uniform sampler2D texture;
					uniform sampler2D texture2;
					uniform sampler2D disp;

					uniform float dispFactor;
					uniform float effectFactor;

					void main() {
						vec2 uv = vUv;

						vec4 disp = texture2D(disp, uv);

						vec2 distortedPosition = vec2(uv.x + dispFactor * (disp.r*effectFactor), uv.y);
						vec2 distortedPosition2 = vec2(uv.x - (1.0 - dispFactor) * (disp.r*effectFactor), uv.y);

						vec4 _texture = texture2D(texture, distortedPosition);
						vec4 _texture2 = texture2D(texture2, distortedPosition2);

						vec4 finalTexture = mix(_texture, _texture2, dispFactor);

						gl_FragColor = finalTexture;

					}
				`;
			case 'slider-textures-vertical-fs':
				return `
					varying vec2 vUv;

					uniform sampler2D texture;
					uniform sampler2D texture2;
					uniform sampler2D disp;

					uniform float dispFactor;
					uniform float effectFactor;

					void main() {
						vec2 uv = vUv;

						vec4 disp = texture2D(disp, uv);

						vec2 distortedPosition = vec2(uv.x, uv.y - dispFactor * (disp.r*effectFactor));
						vec2 distortedPosition2 = vec2(uv.x, uv.y + (1.0 - dispFactor) * (disp.r*effectFactor));

						vec4 _texture = texture2D(texture, distortedPosition);
						vec4 _texture2 = texture2D(texture2, distortedPosition2);

						vec4 finalTexture = mix(_texture, _texture2, dispFactor);

						gl_FragColor = finalTexture;

					}
				`;
			default:
				return false;
		}
	}

	_getShaderMaterial() {
		const fsID = this.slider.params.direction === 'horizontal' ? 'slider-textures-horizontal-fs' : 'slider-textures-vertical-fs';

		return new THREE.ShaderMaterial({
			uniforms: this.uniforms,
			vertexShader: this._getVertexShader('slider-textures-vs'),
			fragmentShader: this._getFragmentShader(fsID),
			opacity: 1
		});
	}

	change({
		from = 0,
		to = 0,
		speed = 1.2,
		intensity = 0.25,
		delay = 0,
		ease = 'power3.out',
		onComplete,
	}) {

		if (!this.material) {
			return false;
		}

		this.material.uniforms.texture.value = this.items[from].texture;
		this.material.uniforms.texture2.value = this.items[to].texture;
		this.material.uniforms.effectFactor.value = intensity;

		this.timeline.fromTo(this.material.uniforms.dispFactor, {
			value: 0
		}, {
			value: 1,
			ease: ease,
			duration: speed,
			delay,
				onComplete: () => {
				if (typeof onComplete === 'function') {
					onComplete();
				}
			}
		});
	}

}
